import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import PropTypes from "prop-types";

// ---------------> styles
const styles = StyleSheet.create({
  page: { padding: 10 },
  section: {
    borderWidth: 1,
    borderColor: "#000",
    borderStyle: "solid",
    padding: 10,
    marginBottom: 2,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  topContainer1: { width: "20%" },
  topContainer2: {
    display: "flex",
    flexDirection: "row",
    width: "39%",
    alignItems: "flex-end",
    gap: 12,
  },
  topContainer3: { width: "25%" },

  header: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 10,
    textAlign: "center",
  },
  text: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 5,
  },
  textM: {
    fontSize: 10,
    fontWeight: "bold",
    marginTop: 40,
  },
  textValue: {
    fontWeight: "normal",
    fontSize: 10,
  },
  table: {
    display: "table",
    width: "100%",
    borderLeft: 1,
    borderColor: "#000",
    borderStyle: "solid",
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#000",
  },
  tableCell: {
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    flexGrow: 1,
    borderRightWidth: 1,
    borderColor: "#000",
    fontWeight: "bold",
  },
  subTableCell: {
    fontSize: 7,
    textAlign: "center",
    padding: 3,
    flexGrow: 1,
    borderRightWidth: 1,
    borderColor: "#000",
  },
  subTableHeader: {
    fontSize: 11,
    fontWeight: "bold",
    textAlign: "center",
    padding: 3,
    flexGrow: 1,
    borderRightWidth: 1,
    borderColor: "#000",
  },
  total: { fontSize: 14, fontWeight: "bold", marginTop: 10 },
});

// Define column configurations
const receiptColumns = [
  { name: "Description", width: "15%" },
  { name: "Gr Wt", width: "10%" },
  { name: "Net Wt", width: "10%" },
  { name: "Fine %", width: "10%" },
  { name: "Wastage %", width: "10%" },
  { name: "Fine", width: "10%" },
  { name: "Amount", width: "10%" },
];
const columnWidths = receiptColumns.map((col) => col.width);

// Reusable components
const InfoText = ({ label, value }) => (
  <Text style={styles.text}>
    {label}: <Text style={styles.textValue}>{value}</Text>
  </Text>
);

const TableHeader = ({ dateWidth, receiptWidth, issueWidth }) => (
  <View style={[styles.tableRow, { borderTopWidth: 1 }]}>
    <Text style={[styles.tableCell, { width: dateWidth }]}>Date</Text>
    <Text style={[styles.subTableHeader, { width: receiptWidth }]}>
      Receipt / Credit
    </Text>
    <Text style={[styles.subTableHeader, { width: issueWidth }]}>
      Issue / Debit
    </Text>
  </View>
);

const SubColumns = ({ columns }) => (
  <View style={styles.tableRow}>
    <Text style={[styles.subTableCell, { width: "10%" }]}></Text>
    {columns.map((col, i) => (
      <Text key={i} style={[styles.subTableCell, { width: col.width }]}>
        {col.name}
      </Text>
    ))}
    {columns.map((col, i) => (
      <Text key={i} style={[styles.subTableCell, { width: col.width }]}>
        {col.name}
      </Text>
    ))}
  </View>
);

const TableRow = ({ date, receipt, issue, columnWidths }) => (
  <View style={styles.tableRow}>
    <Text style={[styles.subTableCell, { width: "10%" }]}>{date}</Text>
    {[
      receipt.description,
      receipt.GrWt,
      receipt.netWt,
      receipt.finePercent,
      receipt.wastagePercent,
      receipt.fine,
      receipt.amount,
    ].map((data, i) => (
      <Text key={i} style={[styles.subTableCell, { width: columnWidths[i] }]}>
        {data}
      </Text>
    ))}
    {[
      issue.description,
      issue.GrWt,
      issue.netWt,
      issue.finePercent,
      issue.wastagePercent,
      issue.fine,
      issue.amount,
    ].map((data, i) => (
      <Text key={i} style={[styles.subTableCell, { width: columnWidths[i] }]}>
        {data}
      </Text>
    ))}
  </View>
);

const TotalRow = ({ totals, columnWidths }) => (
  <View style={[styles.tableRow, { backgroundColor: "#f2f2f2" }]}>
    <Text style={[styles.subTableCell, { width: "10%", fontWeight: "bold" }]}>
      Total
    </Text>
    {[
      "",
      totals.receipt.GrWt,
      totals.receipt.netWt,
      `${totals.receipt.finePercent}%`,
      `${totals.receipt.wastagePercent}%`,
      totals.receipt.fine,
      totals.receipt.amount,
    ].map((total, i) => (
      <Text
        key={i}
        style={[
          styles.subTableCell,
          { width: columnWidths[i], fontWeight: "bold" },
        ]}
      >
        {total}
      </Text>
    ))}
    {[
      "",
      totals.issue.GrWt,
      totals.issue.netWt,
      `${totals.issue.finePercent}%`,
      `${totals.issue.wastagePercent}%`,
      totals.issue.fine,
      totals.issue.amount,
    ].map((total, i) => (
      <Text
        key={i}
        style={[
          styles.subTableCell,
          { width: columnWidths[i], fontWeight: "bold" },
        ]}
      >
        {total}
      </Text>
    ))}
  </View>
);

// Main component
export const CustomerTransactionReport = ({ data }) => {
  if (!data) return null;

  const {
    items,
    totals,
    customer,
    city,
    totalCash,
    totalGoldBalance,
    totalSilverBalance,
    date,
    contact,
    advanceAmount,
  } = data;

  return (
    <Document>
      <Page size={{ width: 842, height: 595 }} style={styles.page}>
        <Text style={styles.header}>Customer Transaction Report</Text>
        <View style={styles.section}>
          <View style={styles.topContainer1}>
            <InfoText label="Customer Name" value={customer} />
            <InfoText label="Address/City" value={city} />
            <div style={{ marginTop: "10px" }}>
              <InfoText label="Total Balance Cash" value={totalCash} />
            </div>
          </View>
          <View style={styles.topContainer2}>
            <InfoText label="Total Balance Gold" value={totalGoldBalance} />
            <InfoText label="Total Balance Silver" value={totalSilverBalance} />
          </View>
          <View style={styles.topContainer3}>
            <InfoText label="Date" value={date} />
            <InfoText label="Contact" value={contact} />
            <div style={{ marginTop: "10px" }}>
              <InfoText label="Advance Amount" value={advanceAmount} />
            </div>
          </View>
        </View>

        <View style={styles.table}>
          <TableHeader dateWidth="4.5%" receiptWidth="45%" issueWidth="45%" />
          <SubColumns columns={receiptColumns} />
          {items.map((item, index) => (
            <TableRow
              key={index}
              date={item.date}
              receipt={item.receipt}
              issue={item.issue}
              columnWidths={columnWidths}
            />
          ))}
          <TotalRow totals={totals} columnWidths={columnWidths} />
        </View>
      </Page>
    </Document>
  );
};

// propstypes
CustomerTransactionReport.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        receipt: PropTypes.shape({
          description: PropTypes.string,
          GrWt: PropTypes.string,
          netWt: PropTypes.string,
          finePercent: PropTypes.string,
          wastagePercent: PropTypes.string,
          fine: PropTypes.string,
          amount: PropTypes.string,
        }),
        issue: PropTypes.shape({
          description: PropTypes.string,
          GrWt: PropTypes.string,
          netWt: PropTypes.string,
          finePercent: PropTypes.string,
          wastagePercent: PropTypes.string,
          fine: PropTypes.string,
          amount: PropTypes.string,
        }),
      })
    ),
    totals: PropTypes.shape({
      receipt: PropTypes.shape({
        GrWt: PropTypes.string,
        netWt: PropTypes.string,
        finePercent: PropTypes.string,
        wastagePercent: PropTypes.string,
        fine: PropTypes.string,
        amount: PropTypes.string,
      }),
      issue: PropTypes.shape({
        GrWt: PropTypes.string,
        netWt: PropTypes.string,
        finePercent: PropTypes.string,
        wastagePercent: PropTypes.string,
        fine: PropTypes.string,
        amount: PropTypes.string,
      }),
    }),
    customer: PropTypes.string,
    city: PropTypes.string,
    totalCash: PropTypes.string,
    totalGoldBalance: PropTypes.string,
    totalSilverBalance: PropTypes.string,
    date: PropTypes.string,
    contact: PropTypes.string,
    advanceAmount: PropTypes.string,
  }),
};
